<template>
  <!--
    Gaveta do sino (ComposedHeader e TrustflixHeader). Coluna flex: cabeçalho e
    rodapé fixos, só a lista rola. A regra global `.sidenav.side-right` (feita
    para o menu do usuário) deixa a gaveta com overflow: hidden — sem um miolo
    rolável a lista ficava presa na primeira tela.
  -->
  <ul :id="id" class="sidenav side-right notify-panel" :style="{ backgroundColor: palette.bg }">
    <li class="notify-panel__header">
      <div class="menu-notify-header container center-align safe-area-top" :style="{ color: palette.text }">
        <a href="javascript:;" class="sidenav-close notify-panel__back" aria-label="Fechar notificações">
          <i class="material-icons left" :style="{ color: accent }">arrow_back</i>
        </a>
        <span>NOTIFICAÇÕES</span>
      </div>
      <div v-if="unread.length" class="notify-panel__actions container">
        <a href="javascript:;" class="notify-panel__mark-all" :style="{ color: accent }" @click="markAllRead">
          Marcar todas como lidas
        </a>
      </div>
    </li>

    <li class="notify-panel__list">
      <template v-if="unreadGroups.length">
        <div v-for="group in unreadGroups" :key="'u-' + group.key" class="notify-group">
          <div class="notify-day container" :style="{ color: palette.muted }">{{ group.label }}</div>
          <div
            v-for="notification in group.items"
            :key="'notification_' + notification.id"
            class="notify-item notify-item--unread sidenav-close"
            :style="{ backgroundColor: palette.unreadBg, borderBottomColor: palette.border }"
            @click="open(notification)"
          >
            <span class="notify-item__dot" :style="{ backgroundColor: accent }" aria-hidden="true"></span>
            <div class="notify-item__body">
              <div class="notify-item__title" :style="{ color: accent }">{{ notification.title }}</div>
              <div class="notify-item__text line-clamp-4" :style="{ color: palette.text }">{{ notification.text }}</div>
              <div class="notify-item__time" :style="{ color: palette.muted }">{{ time(notification) }}</div>
            </div>
            <!-- .stop: sem ele o toque também abriria a notificação e o
                 .sidenav-close do item fecharia a gaveta. -->
            <button
              type="button"
              class="notify-item__dismiss"
              :style="{ color: palette.muted }"
              aria-label="Marcar como lida"
              title="Marcar como lida"
              @click.stop="dismiss(notification)"
            >
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>
      </template>
      <p v-else class="notify-empty container" :style="{ color: palette.muted }">
        Nenhuma notificação nova.
      </p>

      <template v-if="showRead && readGroups.length">
        <div class="notify-section container" :style="{ color: palette.muted, borderTopColor: palette.border }">Lidas</div>
        <div v-for="group in readGroups" :key="'r-' + group.key" class="notify-group">
          <div class="notify-day container" :style="{ color: palette.muted }">{{ group.label }}</div>
          <div
            v-for="notification in group.items"
            :key="'notification_' + notification.id"
            class="notify-item notify-item--read sidenav-close"
            :style="{ borderBottomColor: palette.border }"
            @click="open(notification)"
          >
            <div class="notify-item__body">
              <div class="notify-item__title" :style="{ color: accent }">{{ notification.title }}</div>
              <div class="notify-item__text line-clamp-4" :style="{ color: palette.text }">{{ notification.text }}</div>
              <div class="notify-item__time" :style="{ color: palette.muted }">{{ time(notification) }}</div>
            </div>
          </div>
        </div>
      </template>
    </li>

    <li v-if="read.length" class="notify-panel__footer" :style="{ borderTopColor: palette.border }">
      <SwitchInput
        class="notify-panel__switch valign-wrapper"
        icon="done_all"
        :label="'Mostrar lidas (' + read.length + ')'"
        :layoutTheme="{ primaryColor: accent }"
        :darkTheme="dark ? 'dark' : ''"
        :value="showRead"
        @input="toggleShowRead"
      />
    </li>
  </ul>
</template>

<script>
import SwitchInput from '@/components/SwitchInput';
import { openNotification } from '@/services/push/notificationLink';
import { groupByDay, splitByRead, timeLabel } from '@/services/notifications/notificationGroups';

const SHOW_READ_KEY = 'ead_bell_show_read';

function readShowRead () {
  try {
    return localStorage.getItem(SHOW_READ_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function hexToRgba (hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return `rgba(5, 100, 177, ${alpha})`;
  const v = parseInt(m[1], 16);
  return `rgba(${(v >> 16) & 255}, ${(v >> 8) & 255}, ${v & 255}, ${alpha})`;
}

export default {
  name: 'NotificationsPanel',
  components: { SwitchInput },
  props: {
    // id da gaveta: é o data-target do sino no header.
    id: { type: String, required: true },
    dark: { type: Boolean, default: false },
    accent: { type: String, default: '#0564b1' }
  },
  data () {
    return { showRead: readShowRead(), now: new Date() };
  },
  computed: {
    notifications () {
      return this.$store.state.notification.notifications || [];
    },
    split () {
      return splitByRead(this.notifications);
    },
    unread () {
      return this.split.unread;
    },
    read () {
      return this.split.read;
    },
    unreadGroups () {
      return groupByDay(this.unread, this.now);
    },
    readGroups () {
      return groupByDay(this.read, this.now);
    },
    palette () {
      return this.dark
        ? { bg: '#0D0D13', text: '#bfbfbf', muted: '#8a8a93', border: 'rgba(255, 255, 255, 0.12)', unreadBg: 'rgba(255, 255, 255, 0.05)' }
        : { bg: '#ffffff', text: '#333333', muted: '#757575', border: 'rgba(0, 0, 0, 0.12)', unreadBg: hexToRgba(this.accent, 0.06) };
    }
  },
  watch: {
    // "Hoje"/"Ontem" relativos a quando a lista chega (o polling e o resume a recarregam).
    notifications () {
      this.now = new Date();
    }
  },
  methods: {
    time (notification) {
      return timeLabel(notification);
    },
    // Marca como lida só a que o aluno tocou (antes, abrir o sino marcava todas).
    open (notification) {
      if (this.$store.state.enroll.isPreview) return;
      if (notification.readOn == null) this.$store.dispatch('notification/read', notification.id);
      openNotification(notification);
    },
    // "x": marca como lida sem abrir e sem fechar a gaveta.
    dismiss (notification) {
      if (this.$store.state.enroll.isPreview) return;
      this.$store.dispatch('notification/read', notification.id);
    },
    markAllRead () {
      if (this.$store.state.enroll.isPreview) return;
      this.unread.forEach(n => this.$store.dispatch('notification/read', n.id));
    },
    toggleShowRead () {
      this.showRead = !this.showRead;
      try {
        localStorage.setItem(SHOW_READ_KEY, this.showRead ? '1' : '0');
      } catch (e) {}
    }
  }
};
</script>

<style scoped lang="scss">
.notify-panel > li {
  line-height: normal;
}

.notify-panel__header {
  flex: none;
}

// Linha própria abaixo do título: a gaveta tem 300px e "NOTIFICAÇÕES" com o
// letter-spacing do cabeçalho não deixa espaço para o link na mesma linha.
.notify-panel__actions {
  padding-bottom: 8px;
  text-align: right;
}

.notify-panel__mark-all {
  font-family: roboto;
  font-size: 13px;
  font-weight: 500;
}

// Só a lista rola (min-height: 0 deixa o item flex encolher abaixo do conteúdo).
.notify-panel__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-bottom: 16px;
}

.notify-panel__footer {
  flex: none;
  border-top: 1px solid;
  padding: 4px 16px env(safe-area-inset-bottom, 0px);
}

.notify-panel__switch {
  margin: 0;
  min-height: 56px;
}

.notify-day,
.notify-section {
  font-family: roboto;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding-top: 16px;
  padding-bottom: 6px;
}

.notify-section {
  border-top: 1px solid;
  margin-top: 16px;
}

.notify-empty {
  font-family: roboto;
  font-size: 14px;
  padding: 24px 0;
}

.notify-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid;
  cursor: pointer;
  font-family: roboto;
  font-size: 13px;
  line-height: 1.45;
}

.notify-item__dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
}

.notify-item__body {
  flex: 1;
  min-width: 0;
}

.notify-item__title {
  font-weight: 500;
}

.notify-item--unread .notify-item__title {
  font-weight: 700;
}

.notify-item__text {
  margin-top: 2px;
  overflow-wrap: anywhere;
}

.notify-item__time {
  margin-top: 4px;
  font-size: 12px;
}

// Área de toque de 40px; a margem negativa alinha o ícone ao título sem
// empurrar o texto.
.notify-item__dismiss {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: -10px -12px 0 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;

  // O Materialize pinta todo button:focus de verde-água.
  &:focus {
    background-color: transparent;
  }

  &:active {
    background-color: rgba(127, 127, 127, 0.15);
  }

  i {
    font-size: 18px;
  }
}

.notify-item--read {
  opacity: 0.65;
}
</style>
